import { useEffect, useRef, useState } from "react";
import { Alert, Button, message, Modal, Progress, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import * as tus from "tus-js-client";
import service from "../../api/FetchInterceptor";

const formatSize = (bytes) => {
    if (!bytes) return "0 Mo";
    const mb = bytes / (1024 * 1024);
    return mb >= 1024 ? `${(mb / 1024).toFixed(2)} Go` : `${mb.toFixed(1)} Mo`;
};

/**
 * Upload direct de la vidéo d'une partie vers Bunny Stream (protocole TUS, reprend après une coupure).
 * Le backend fournit une autorisation temporaire ; la clé API Bunny n'arrive jamais dans le navigateur.
 */
const VideoUpload = ({ part, open, setOpen, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploaded, setUploaded] = useState(0);
    const [state, setState] = useState("idle"); // idle | preparing | uploading | finishing | error
    const [error, setError] = useState(null);
    const uploadRef = useRef(null);

    const busy = ["preparing", "uploading", "finishing"].includes(state);

    useEffect(() => {
        if (open) {
            setFile(null);
            setProgress(0);
            setUploaded(0);
            setState("idle");
            setError(null);
        }
    }, [open]);

    // Empêche de fermer l'onglet par erreur pendant un upload.
    useEffect(() => {
        if (!busy) return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [busy]);

    const fail = (msg) => {
        setState("error");
        setError(msg);
    };

    const start = async () => {
        if (!file || !part) return;
        setError(null);
        setState("preparing");

        let auth;
        try {
            auth = await service({ method: "POST", url: `admin/parts/${part.id}/video` });
        } catch (e) {
            fail(e?.response?.data?.message ?? "Impossible de préparer l'upload.");
            return;
        }

        setState("uploading");

        const upload = new tus.Upload(file, {
            endpoint: auth.endpoint,
            retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
            chunkSize: 50 * 1024 * 1024,
            headers: {
                AuthorizationSignature: auth.signature,
                AuthorizationExpire: String(auth.expires),
                VideoId: auth.video_id,
                LibraryId: String(auth.library_id),
            },
            metadata: {
                filetype: file.type,
                title: part.title,
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                setUploaded(bytesUploaded);
                setProgress(Math.floor((bytesUploaded / bytesTotal) * 100));
            },
            onError: (err) => {
                console.error("Upload Bunny", err);
                fail("L'upload a échoué. Vérifiez votre connexion puis réessayez.");
            },
            onSuccess: async () => {
                setState("finishing");
                try {
                    await service({
                        method: "POST",
                        url: `admin/parts/${part.id}/video/complete`,
                        data: { video_id: auth.video_id },
                    });
                    message.success("Vidéo envoyée — Bunny l'encode, elle sera prête dans quelques minutes.");
                    setOpen(false);
                    onSuccess?.();
                } catch (e) {
                    fail(e?.response?.data?.message ?? "La vidéo est envoyée mais n'a pas pu être rattachée à la partie.");
                }
            },
        });

        uploadRef.current = upload;
        upload.start();
    };

    const cancel = () => {
        if (state === "uploading" && uploadRef.current) {
            uploadRef.current.abort(true);
        }
        setOpen(false);
    };

    return (
        <Modal
            title={part?.bunny_video_id ? `Remplacer la vidéo — ${part?.title}` : `Ajouter la vidéo — ${part?.title}`}
            open={open}
            onCancel={cancel}
            maskClosable={!busy}
            closable={!busy || state === "uploading"}
            destroyOnHidden
            footer={[
                <Button key="cancel" onClick={cancel} disabled={state === "preparing" || state === "finishing"}>
                    {state === "uploading" ? "Annuler l'upload" : "Fermer"}
                </Button>,
                <Button key="start" type="primary" onClick={start} disabled={!file || busy} loading={busy}>
                    {state === "error" ? "Réessayer" : "Envoyer"}
                </Button>,
            ]}
        >
            {part?.bunny_video_id && state === "idle" && (
                <Alert
                    className="mb-4"
                    type="warning"
                    showIcon
                    message="La vidéo actuelle sera remplacée une fois la nouvelle entièrement envoyée."
                />
            )}

            <Upload.Dragger
                accept="video/*"
                maxCount={1}
                multiple={false}
                disabled={busy}
                showUploadList={false}
                beforeUpload={(f) => {
                    setFile(f);
                    setState("idle");
                    setError(null);
                    return false;
                }}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                {file ? (
                    <>
                        <p className="ant-upload-text">{file.name}</p>
                        <p className="ant-upload-hint">{formatSize(file.size)} — cliquez pour choisir un autre fichier</p>
                    </>
                ) : (
                    <>
                        <p className="ant-upload-text">Cliquez ou déposez la vidéo ici</p>
                        <p className="ant-upload-hint">MP4 conseillé. Les gros fichiers sont envoyés par morceaux et reprennent après une coupure.</p>
                    </>
                )}
            </Upload.Dragger>

            {(state === "uploading" || state === "finishing") && (
                <div className="mt-4">
                    <Progress percent={progress} status="active" />
                    <p className="m-0 text-xs text-gray-500">
                        {state === "finishing"
                            ? "Finalisation…"
                            : `${formatSize(uploaded)} / ${formatSize(file?.size)} — gardez cette fenêtre ouverte.`}
                    </p>
                </div>
            )}

            {error && <Alert className="mt-4" type="error" showIcon message={error} />}
        </Modal>
    );
};

export default VideoUpload;
